import { Box, Button, Group, Skeleton, Table, Text } from "@mantine/core"
import { LockIcon, MagnifyingGlassIcon, TrayIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import type { IPermissionCode } from "../permission"
import classes from "./state-views.module.css"

/**
 * DeHUB UI стандартын жагсаалтын 5 төлөв (StdStates): хоосон, хайлтад олдсонгүй, ачаалж буй,
 * алдаа, эрхгүй. Хүснэгттэй хуудас бүр эдгээрийг ижил хэлбэрээр харуулна.
 */
export type ListStateKind = "empty" | "noResults" | "loading" | "error" | "denied"

type ListStateProps = Readonly<{
  state: ListStateKind
  title?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  /** empty: үндсэн үйлдэл; noResults: «Шүүлт цэвэрлэх» (label орхивол анхдагч). */
  action?: { label?: string; onClick: () => void }
  /** error: «Дахин оролдох». */
  onRetry?: () => void
  /** error: HTTP эсвэл серверийн код. */
  errorCode?: string | number
  /** denied: шаардлагатай эрхийн код. */
  permission?: IPermissionCode | string
  /** loading: skeleton баганын тоо (анхдагч 4). */
  columns?: number
  rows?: number
}>

export function ListState({ state, title, description, icon, action, onRetry, errorCode, permission, columns = 4, rows = 5 }: ListStateProps) {
  const { t } = useTranslation("common")

  if (state === "loading") {
    return (
      <Table aria-busy="true" aria-label={t("loading", { defaultValue: "Ачаалж байна" })}>
        <Table.Tbody>
          {Array.from({ length: rows }, (_, r) => (
            <Table.Tr key={r} className={classes.skeletonRow}>
              {Array.from({ length: columns }, (_, c) => (
                <Table.Td key={c}>
                  <Skeleton height={14} radius="sm" width={`${40 + ((r * 7 + c * 13) % 40)}%`} />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (state === "error") {
    return (
      <Box className={classes.errorBox}>
        <Box role="alert" className={classes.alert}>
          <Box component="span" className={classes.dot} />
          <Box>
            <Text className={classes.alertTitle}>{title ?? t("state.error.title")}</Text>
            <Text className={classes.text}>{description ?? t("state.error.text")}</Text>
          </Box>
        </Box>
        {(onRetry || errorCode != null) && (
          <Group gap="sm">
            {onRetry && <Button onClick={onRetry}>{t("state.error.retry")}</Button>}
            {errorCode != null && <Text className={classes.code}>{t("state.error.code", { code: errorCode })}</Text>}
          </Group>
        )}
      </Box>
    )
  }

  if (state === "denied") {
    return <DeniedCard permission={permission ?? ""} title={title} />
  }

  const isEmpty = state === "empty"
  return (
    <Box className={classes.center}>
      <Box component="span" className={classes.iconBox}>
        {icon ?? (isEmpty ? <TrayIcon size={24} /> : <MagnifyingGlassIcon size={24} />)}
      </Box>
      <Text className={classes.title}>{title ?? t(isEmpty ? "state.empty.title" : "state.noResults.title")}</Text>
      {(description ?? (!isEmpty && t("state.noResults.text"))) && (
        <Text className={classes.text}>{description ?? t("state.noResults.text")}</Text>
      )}
      {action && (
        <Button variant={isEmpty ? "filled" : "default"} onClick={action.onClick}>
          {action.label ?? (isEmpty ? "" : t("state.noResults.clear"))}
        </Button>
      )}
    </Box>
  )
}

/** Эрхгүй үед улаан Alert биш тайван саарал карт. Эрхийн кодыг хэрэглэгчид харуулахгүй. */
export function DeniedCard({ permission, title }: Readonly<{ permission: IPermissionCode | string; title?: ReactNode }>) {
  const { t } = useTranslation("common")
  return (
    <Box className={classes.denied}>
      <Box component="span" className={classes.deniedIcon}>
        <LockIcon size={22} />
      </Box>
      <Box>
        <Text fz={15} fw={650}>
          {title ?? t("permission.deniedTitle")}
        </Text>
        <Text fz={14} c="gray.8">
          {permission ? `${t("permission.required")} ` : null}
          {t("permission.contact")}
        </Text>
      </Box>
    </Box>
  )
}
